import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { loadParishHelp } from "../lib/parishHelp";
import { useParish } from "../lib/parishContext";
import Lightbox from "../components/Lightbox";
import ParishLogo from "../components/ParishLogo";
import PoweredByFooter from "../components/PoweredByFooter";
import NotFound from "./NotFound";
import { reportVolunteerEvent } from "../lib/volunteerEvents";

interface LightboxState {
  src: string;
  alt: string;
}

export default function ParishHelp() {
  const { parish: parishSlug } = useParams();
  const location = useLocation();
  const help = useMemo(
    () => (parishSlug ? loadParishHelp(parishSlug) : null),
    [parishSlug],
  );
  const { parish } = useParish();
  const sectionsRef = useRef<HTMLUListElement | null>(null);
  const introRef = useRef<HTMLDivElement | null>(null);

  // Initial open state — hash-target only when present, else first
  // section by default.
  const [openIds, setOpenIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (!help) return initial;
    const hash = location.hash.replace(/^#/, "");
    if (hash && help.sections.some((s) => s.id === hash)) {
      initial.add(hash);
      return initial;
    }
    if (help.sections[0]) initial.add(help.sections[0].id);
    return initial;
  });

  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  // Adoption signal: a SIGNED-IN volunteer opened the help center.
  // Signed-out visitors report nothing (reportVolunteerEvent no-ops
  // without a session) — instrumenting public pages is out of scope.
  // Fires once per mount, not per section toggle.
  useEffect(() => {
    if (!help) return;
    void reportVolunteerEvent("help_used");
  }, [help]);

  useEffect(() => {
    if (!help) return;
    const hash = location.hash.replace(/^#/, "");
    if (!hash) return;
    if (!help.sections.some((s) => s.id === hash)) return;
    setOpenIds((prev) => {
      if (prev.has(hash)) return prev;
      const next = new Set(prev);
      next.add(hash);
      return next;
    });
    const el = document.getElementById(`help-section-${hash}`);
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "auto", block: "start" });
      });
    }
  }, [help, location.hash]);

  useEffect(() => {
    if (!help) return;
    const original = document.title;
    const title = parish.display_name || help.parishName;
    document.title = `Volunteer Help — ${title}`;
    return () => {
      document.title = original;
    };
  }, [help, parish.display_name]);

  // Delegate clicks on rendered help-section images to the lightbox
  // so we don't have to parse the marked HTML and rewrap each <img>.
  useEffect(() => {
    const onClick = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!(target instanceof HTMLImageElement)) return;
      const closest =
        target.closest(".parish-help__section-body") ||
        target.closest(".parish-help__intro");
      if (!closest) return;
      setLightbox({ src: target.src, alt: target.alt || "Image" });
    };
    const sections = sectionsRef.current;
    const intro = introRef.current;
    sections?.addEventListener("click", onClick);
    intro?.addEventListener("click", onClick);
    return () => {
      sections?.removeEventListener("click", onClick);
      intro?.removeEventListener("click", onClick);
    };
  }, [help]);

  const toggle = useCallback((id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (window.location.hash === `#${id}`) {
          history.replaceState(null, "", window.location.pathname);
        }
      } else {
        next.add(id);
        history.replaceState(null, "", `${window.location.pathname}#${id}`);
      }
      return next;
    });
  }, []);

  if (!help) {
    return <NotFound />;
  }

  return (
    <main className="page page--parish-help">
      <header className="parish-help__band">
        <ParishLogo size={48} />
      </header>

      <section className="parish-help__hero">
        <h1 className="parish-help__title">Volunteer Help</h1>
        {help.introHtml ? (
          <div
            ref={introRef}
            className="parish-help__intro"
            dangerouslySetInnerHTML={{ __html: help.introHtml }}
          />
        ) : null}
      </section>

      <ul ref={sectionsRef} className="parish-help__sections">
        {help.sections.map((section) => {
          const isOpen = openIds.has(section.id);
          return (
            <li
              key={section.id}
              id={`help-section-${section.id}`}
              className={`parish-help__section${isOpen ? " is-open" : ""}`}
            >
              <button
                type="button"
                className="parish-help__section-trigger"
                aria-expanded={isOpen}
                aria-controls={`help-panel-${section.id}`}
                onClick={() => toggle(section.id)}
              >
                <span className="parish-help__section-title">{section.title}</span>
                <span className="parish-help__chevron" aria-hidden="true">
                  {isOpen ? "▾" : "▸"}
                </span>
              </button>
              {isOpen ? (
                <div
                  id={`help-panel-${section.id}`}
                  className="parish-help__section-body"
                  dangerouslySetInnerHTML={{ __html: section.html }}
                />
              ) : null}
            </li>
          );
        })}
      </ul>

      <PoweredByFooter />

      {lightbox ? (
        <Lightbox
          src={lightbox.src}
          alt={lightbox.alt}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </main>
  );
}
